import React from "react";
import {Box, Drawer, IconButton, Tooltip, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MergedDocumentViewer from "./MergedDocumentViewer";

/**
 * The workspace the Procedure Construct decision is taken in: a panel that slides in from the
 * right over most of the screen, split into a document column and a decision column.
 *
 * The documents own the left column outright, top to bottom - the decision is a reading task
 * before it is a data-entry task, so the bill gets the full height of the screen. Everything
 * that belongs to the decision itself lives in the right column, in the order it is read and
 * used: what this is, what the claim saves today and the one button that commits it, then the
 * lists it is taken on.
 */

/**
 * The two halves are sized against the screen, not against each other: the documents take
 * 40% of the screen and the decision column 55%, which is what the panel spans in total.
 * The split the drag starts from is those shares expressed inside the panel.
 */
const DOCUMENT_SCREEN_PERCENT = 40;
const DECISION_SCREEN_PERCENT = 55;
const DEFAULT_WIDTH_PERCENT = DOCUMENT_SCREEN_PERCENT + DECISION_SCREEN_PERCENT;
const DEFAULT_SPLIT_PERCENT = (DOCUMENT_SCREEN_PERCENT / DEFAULT_WIDTH_PERCENT) * 100;
const MIN_SPLIT_PERCENT = 25;
const MAX_SPLIT_PERCENT = 70;

const titleBarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
    padding: '10px 14px',
    background: 'linear-gradient(90deg, #e9f1fb 0%, #f7fbff 55%, #edf5ff 100%)',
    borderBottom: '1px solid #dce7f4',
};

const brandBadgeStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    flexShrink: 0,
    borderRadius: '10px',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6d5ce7 0%, #4a3fd0 100%)',
    boxShadow: '0 2px 6px rgba(74, 63, 208, 0.35)',
};

const closeButtonStyle = {
    width: '30px',
    height: '30px',
    flexShrink: 0,
    borderRadius: '8px',
    color: '#5c7086',
    background: '#ffffff',
    border: '1px solid #dce7f4',
};

// The claim's standing saving and the button that changes it, side by side on one band.
const figuresBandStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexShrink: 0,
    padding: '10px 14px',
    background: 'linear-gradient(90deg, #f1f7fd 0%, #fbfdff 100%)',
    borderBottom: '1px solid #e6edf6',
};

const figurePillStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: '124px',
    padding: '6px 12px',
    borderRadius: '10px',
    background: '#ffffff',
    border: '1px solid #dbe6f3',
    boxShadow: '0 1px 2px rgba(18, 49, 91, 0.05)',
};

const figureLabelStyle = {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#8195ad',
};

const figureValueStyle = {fontSize: '1.02rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums'};

const chipBaseStyle = {
    fontSize: '0.62rem',
    fontWeight: 700,
    borderRadius: '999px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
};

/**
 * A figure carries as many chips as it has things to say about the one amount beside it - what
 * it is worth as a share of the claim, how many rows it is totalling. They read as one row of
 * pills, told apart by tone rather than by position: money in green, a count in the panel's own
 * blue, so a glance never mistakes a line count for a percentage.
 */
const chipToneStyles = {
    money: {color: '#1b7f4b', background: '#e6f6ec', border: '1px solid #c3e7d1'},
    count: {color: '#3f5b7e', background: '#eef4fb', border: '1px solid #d5e3f3'},
};

/**
 * The chips a figure is asking for, in one shape. `chip: 'x'`, `chips: ['x', 'y']` and
 * `chips: [{text, tone}]` all leave here as the last of the three, with the empties dropped -
 * a caller may hand over a slot it had nothing to put in.
 */
const figureChips = (figure) => (figure.chips || (figure.chip ? [figure.chip] : []))
    .filter(Boolean)
    .map((chip) => (typeof chip === 'string' ? {text: chip} : chip))
    .filter((chip) => chip && chip.text);

function PcNonPayablesWorkspace(props) {
    const {
        open,
        onClose,
        claimId,
        documentFiles,
        title,
        subtitle,
        figures,
        actions,
        widthPercent = DEFAULT_WIDTH_PERCENT,
        children,
    } = props;

    const [splitPercent, setSplitPercent] = React.useState(DEFAULT_SPLIT_PERCENT);
    const [dragging, setDragging] = React.useState(false);
    const bodyRef = React.useRef(null);
    const draggingRef = React.useRef(false);

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onMove = (event) => {
            if (!draggingRef.current) {
                return;
            }
            const rect = bodyRef.current?.getBoundingClientRect();
            if (!rect || !(rect.width > 0)) {
                return;
            }
            const percent = ((event.clientX - rect.left) / rect.width) * 100;
            setSplitPercent(Math.min(Math.max(percent, MIN_SPLIT_PERCENT), MAX_SPLIT_PERCENT));
        };
        const onUp = () => {
            if (draggingRef.current) {
                draggingRef.current = false;
                setDragging(false);
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [open]);

    const startDrag = (event) => {
        event.preventDefault();
        draggingRef.current = true;
        setDragging(true);
    };

    // While the split is being dragged neither half may swallow the pointer - an iframe in the
    // document column would otherwise eat the mousemove.
    const paneEvents = dragging ? 'none' : 'auto';

    return (
        <Drawer
            anchor="right"
            open={Boolean(open)}
            onClose={onClose}
            transitionDuration={{enter: 320, exit: 240}}
            PaperProps={{sx: {width: `${widthPercent}%`, maxWidth: '100%', overflow: 'hidden'}}}
        >
            <Box
                ref={bodyRef}
                style={{
                    display: 'flex',
                    height: '100%',
                    minHeight: 0,
                    cursor: dragging ? 'col-resize' : 'default',
                    userSelect: dragging ? 'none' : 'auto',
                }}
            >
                <Box style={{
                    flex: `0 0 ${splitPercent}%`,
                    minWidth: 0,
                    height: '100%',
                    pointerEvents: paneEvents,
                }}>
                    <MergedDocumentViewer claimId={claimId} files={documentFiles}/>
                </Box>

                <Tooltip title="Drag to resize" placement="right">
                    <Box
                        onMouseDown={startDrag}
                        style={{
                            flex: '0 0 6px',
                            cursor: 'col-resize',
                            background: dragging ? '#90caf9' : '#eeeeee',
                            borderLeft: '1px solid #e0e0e0',
                            borderRight: '1px solid #e0e0e0',
                        }}
                    />
                </Tooltip>

                <Box style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#ffffff',
                    pointerEvents: paneEvents,
                }}>
                    <Box style={titleBarStyle}>
                        <Box style={brandBadgeStyle}>
                            <HealthAndSafetyIcon style={{fontSize: '1.15rem'}}/>
                        </Box>
                        <Box style={{minWidth: 0, flexGrow: 1}}>
                            <Typography style={{
                                fontSize: '0.98rem',
                                fontWeight: 700,
                                color: '#12315b',
                                lineHeight: 1.25,
                            }}>
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography style={{fontSize: '0.72rem', color: '#6b7f99'}}>{subtitle}</Typography>
                            )}
                        </Box>
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={onClose} style={closeButtonStyle}>
                                <CloseIcon style={{fontSize: '0.95rem'}}/>
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box style={figuresBandStyle}>
                        <Box style={{display: 'flex', flexWrap: 'wrap', gap: '10px', minWidth: 0}}>
                            {(Array.isArray(figures) ? figures : []).map((figure) => (
                                <Tooltip key={figure.label} title={figure.tooltip || ''}>
                                    <Box style={figurePillStyle}>
                                        <Typography style={figureLabelStyle}>{figure.label}</Typography>
                                        <Box style={{display: 'flex', alignItems: 'baseline', gap: '6px'}}>
                                            <Typography style={{
                                                ...figureValueStyle,
                                                color: figure.color || '#12315b',
                                            }}>
                                                {figure.value}
                                            </Typography>
                                            {figureChips(figure).map((chip) => (
                                                <Box
                                                    key={chip.text}
                                                    style={{
                                                        ...chipBaseStyle,
                                                        ...(chipToneStyles[chip.tone] || chipToneStyles.money),
                                                    }}
                                                >
                                                    {chip.text}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                        {actions && <Box style={{flexShrink: 0}}>{actions}</Box>}
                    </Box>

                    <Box style={{flexGrow: 1, minHeight: 0, overflow: 'auto', padding: '12px 14px 16px'}}>
                        {children}
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
}

export default PcNonPayablesWorkspace;
